import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"
import { reloadWithWalletTabIfNeeded } from "../utils/wallet_tab_return_url"

export default class extends Controller {
  // 💡 ターゲット: 登録ボタン、そのテキスト表示部分はスタッフ用モーダルにのみ存在
  static targets = ["cellsContainer", "cardId", "ticketName", "shopId", "treatmentId", "registerButton", "registerButtonText"]

  // 💡 値: customerViewValueは削除し、isCustomerViewプロパティで処理
  static values = {
    loadingMessage: String,
    loadErrorMessage: String,
    saveErrorMessage: String,
    deleteErrorMessage: String,
    isActive: Boolean, // 回数券のアクティブ状態を保持
    registerButtonActiveText: String,
    registerButtonInactiveText: String,
    cellsUrl: String,
    createUrl: String,
    updateUrl: String,
    destroyUrl: String,
    customerId: Number,
    confirmDeleteMessage: String
  }

  currentLocale = 'ja';
  // 💡 追加: 顧客ビュー判定フラグを内部プロパティとして保持
  isCustomerView = false; 

  connect() {
    this.currentLocale = document.documentElement.lang || 'ja';
    this.cellsDirty = false;

    this.element.addEventListener('show.bs.modal', (event) => {
      this.cellsDirty = false;
      const button = event.relatedTarget

      // 💡 クリーンアップ: dataset.isCustomerView の取得とcustomerViewValueへの保存を削除
      // 💡 新しい判定ロジック: モーダルのIDに基づいて閲覧モードを判定
      this.isCustomerView = this.element.id === 'ticketCellModalCustomer';
      
      const isActive = button.dataset.isActive === 'true'
      this.isActiveValue = isActive

      this.cardIdTarget.value = button.dataset.ticketCardId
      this.ticketNameTarget.textContent = button.dataset.ticketName
      this.shopIdTarget.value = button.dataset.shopId

      // 💡 登録ボタン制御: 顧客ビューモーダルにはそもそもボタンがないため、スタッフ用モーダルのみに適用
      if (!this.isCustomerView) {
          this.controlRegisterButton()
      }
      
      // 💡 クリーンアップ: cellsContainerTargetへのclassList.toggleを削除 (サーバー側でHTMLを分けるため不要)

      this.loadCells()
    })

    this.element.addEventListener('hidden.bs.modal', () => {
      if (!this.cellsDirty) return;

      this.cellsDirty = false;
      reloadWithWalletTabIfNeeded();
    });
  }

  get shopId() {
    return this.shopIdTarget.value;
  }

  // ----------------------------------------------------
  // ユーティリティ: 登録ボタンの状態制御 (スタッフ用モーダルでのみ実行)
  // ----------------------------------------------------
  controlRegisterButton() {
    if (this.hasRegisterButtonTarget) {
      // 💡 クリーンアップ: this.customerViewValueによる二重チェックを削除
      this.registerButtonTarget.disabled = !this.isActiveValue 

      if (this.hasRegisterButtonTextTarget) {
        this.registerButtonTextTarget.textContent = this.isActiveValue
          ? this.registerButtonActiveTextValue
          : this.registerButtonInactiveTextValue
      }
    }
  }

  // ----------------------------------------------------
  // 1. サーバーからセルのHTMLフラグメントを読み込み、コンテナに挿入する
  // ----------------------------------------------------
  loadCells() {
    const cardId = this.cardIdTarget.value
    this.cellsContainerTarget.innerHTML = `<div class="text-center w-100 text-primary"><i class="fas fa-spinner fa-spin me-2"></i>${this.loadingMessageValue}</div>`

    const baseUrl = this.cellsUrlValue.replace("__CARD__", cardId)
    const query = {
      customer_id: this.customerIdValue,
      treatment_id: this.hasTreatmentIdTarget ? this.treatmentIdTarget.value : null
    }
    if (this.isCustomerView) query.customer_view = "true"
    const url = this.withQuery(baseUrl, query)

    fetch(url, {
      headers: { "Accept": "text/html" }
    })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.text();
    })
    .then(html => {
      this.cellsContainerTarget.innerHTML = html
    })
    .catch(error => {
      console.error("セルデータの読み込みに失敗:", error)
      this.cellsContainerTarget.innerHTML = `<p class='text-danger w-100'>⚠️ ${this.loadErrorMessageValue}</p>`
    })
  }

  // ----------------------------------------------------
  // 2. 利用を登録する (新しい TicketUsage を作成)
  // ----------------------------------------------------
  registerUsage() {
    // 💡 ガード句: 顧客ビューの場合は処理をブロック（必須）
    if (this.isCustomerView) {
        console.warn("顧客閲覧モードでは利用登録はできません。");
        return;
    }
    
    // ... (既存の登録ロジック)
    const cardId = this.cardIdTarget.value
    const treatmentId = this.treatmentIdTarget.value
    if (!treatmentId) {
      this.showToast(this.saveErrorMessageValue, "danger")
      return
    }
    const url = this.createUrlValue

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector("meta[name=csrf-token]").content,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: JSON.stringify({
        ticket_usage: {
          master_treatment_id: cardId,
          treatment_id: treatmentId
        }
      })
    })
    .then(response => {
      if (!response.ok) return response.text().then(message => { throw new Error(message || this.saveErrorMessageValue) })
      return response.text()
    })
    .then(html => {
      Turbo.renderStreamMessage(html)
      this.cellsDirty = true
    })
    .catch(error => {
      console.error(this.saveErrorMessageValue, error)
      this.showToast(error.message, "danger")
    })
  }

  // ----------------------------------------------------
  // 3. 利用をキャンセルする (TicketUsage を削除)
  // ----------------------------------------------------
  deleteUsage(event) {
    // 💡 ガード句: 顧客ビューの場合は処理をブロック（必須）
    if (this.isCustomerView) {
        console.warn("顧客閲覧モードでは利用履歴の削除はできません。");
        return;
    }
    
    const usageId = event.currentTarget.dataset.usageId

    if (!usageId) {
        console.error("TicketUsage ID が見つかりません。");
        return;
    }

    const url = this.withQuery(
      this.destroyUrlValue.replace("__USAGE__", usageId),
      {
        customer_id: this.customerIdValue,
        treatment_id: this.hasTreatmentIdTarget ? this.treatmentIdTarget.value : null
      }
    )

    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector("meta[name=csrf-token]").content,
        "Accept": "text/vnd.turbo-stream.html"
      }
    })
    .then(response => {
      if (!response.ok) return response.text().then(message => { throw new Error(message || this.deleteErrorMessageValue) })
      return response.text()
    })
    .then(html => {
      Turbo.renderStreamMessage(html)
      this.cellsDirty = true
    })
    .catch(error => {
      console.error(this.deleteErrorMessageValue, error)
      this.showToast(error.message, "danger")
    })
  }

  // ----------------------------------------------------
  // 4. 利用情報を更新する (利用日・メモ)
  // ----------------------------------------------------
  updateUsage(event) {
    // 💡 ガード句: 顧客ビューの場合は処理をブロック（必須）
    if (this.isCustomerView) {
        console.warn("顧客閲覧モードでは利用履歴の更新はできません。");
        return;
    }
    
    // ... (既存の更新ロジック)
    const target = event.currentTarget;
    const usageId = target.dataset.usageId;
    const fieldName = target.dataset.fieldName;

    const url = this.withQuery(
      this.updateUrlValue.replace("__USAGE__", usageId),
      {
        customer_id: this.customerIdValue,
        treatment_id: this.hasTreatmentIdTarget ? this.treatmentIdTarget.value : null
      }
    )

    const updateData = {};
    updateData[fieldName] = target.value;

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector("meta[name=csrf-token]").content,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: JSON.stringify({
        ticket_usage: updateData
      })
    })
    .then(response => {
      if (!response.ok) return response.text().then(message => { throw new Error(message || this.saveErrorMessageValue) })
      return response.text()
    })
    .then(html => {
      Turbo.renderStreamMessage(html)
      this.cellsDirty = true
    })
    .catch(error => {
      console.error(this.saveErrorMessageValue, error)
      this.showToast(error.message, "danger")
    })
  }

  showToast(message, type = "success") {
    let container = document.querySelector(".toast-container")
    if (!container) {
      container = document.createElement("div")
      container.className = "toast-container position-fixed top-0 end-0 p-3"
      container.style.zIndex = "2000"
      document.body.appendChild(container)
    }

    const toastElement = document.createElement("div")
    toastElement.className = `toast align-items-center text-bg-${type} border-0`
    toastElement.setAttribute("role", "alert")
    toastElement.innerHTML = `
      <div class="d-flex">
        <div class="toast-body"></div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
      </div>`
    toastElement.querySelector(".toast-body").textContent = message
    container.appendChild(toastElement)

    const toast = new bootstrap.Toast(toastElement, { delay: 4000 })
    toastElement.addEventListener("hidden.bs.toast", () => toastElement.remove(), { once: true })
    toast.show()
  }

  withQuery(path, params) {
    const url = new URL(path, window.location.origin)
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") url.searchParams.set(key, value)
    })
    return `${url.pathname}${url.search}`
  }
}